import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { DiagnosticOrderActionsService } from '@ispm/dashboard/test/services/diagnostic-order-actions.service';
import { PdfService } from '@ispm/dashboard/test/services/pdf.service';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { AttachmentService } from '@ispm/migration/services/attachment.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { TranslateService } from '@ngx-translate/core';
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { FileService } from "@ispm-client/modules/file-module/services/file.service";
import { EMPTY, of } from 'rxjs';
import {catchError, map, take, filter} from 'rxjs/operators';
import { CryptoService } from "@ispm/migration/services/crypto.service";
import { ProgressionService } from "@ispm/shared/services/progression.service";
import { Subscription } from "rxjs/Subscription";
import { forkJoin } from "rxjs/observable/forkJoin";
import { Progression } from "@ispm-shared/modules/common";
import { IDocument } from "@ispm/migration/interfaces/report-and-document.interface";
import { IDocumentAction } from "@ispm/migration/interfaces/document-action.interface";
import { ExtendedHttpResponse } from "@ispm/core/services/http-interceptor.service";

@Component({
  selector: 'gen-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.scss'],
})
export class AttachmentComponent implements OnInit, OnDestroy {

  @Input() set currentRevision(currentRevision: any) {
    this.currentRevisionData = currentRevision;
  }

  @Input() testNumber: any;
  @Input() workflowId: string;

  @Output() documentsLoaded: EventEmitter<boolean> = new EventEmitter();

  private $subscription: Subscription = new Subscription();
  private actions: IDocumentAction[] = [];
  private currentRevisionData: any;
  private externalId: any;
  private fileTypes = {};
  private headers: any = {};
  private interval: any;
  private ordererReference: any;
  private organization: any;
  private postReportToEmrEnabled: any;
  private postReportToEmrOnGenerateLetterEnabled: any;
  private shareWithOrganizationId: any;
  private userId: any;
  private xsrf: any;

  public files: IDocument[] = [];
  public lists: IDocument[] = [];
  public maxUploadedFile: any;
  public actionsLabelObject = {
    copyUrl: {text: 'Copy file Url', shouldUpdate: false},
    delete: {text: 'Remove', shouldUpdate: false},
    download: {text: 'Download', shouldUpdate: false},
    postToEmr: {text: 'Post to EMR', shouldUpdate: false},
    restrict: {text: 'Stop sharing', delimiterWord: ' with ', shouldUpdate: true},
    share: {text: 'Share', delimiterWord: ' with ', shouldUpdate: true},
  };
  public fileSizeErrorMessage: string;
  public file: any;
  public apxFileSz: string;
  public value: number;

  constructor(
    private testService: TestService,
    private workflowService: WorkflowService,
    public attachmentsService: AttachmentService,
    private authService: AuthenticationService,
    private userActionCreatorService: UserActionCreatorService,
    private modalLoader: DynamicModalLoaderService,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private pdfService: PdfService,
    private coreActionCreator: CoreActionCreatorService,
    private translateService: TranslateService,
    private notificationMessageService: NotificationMessageService,
    private changeDetect: ChangeDetectorRef,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private fileService: FileService,
    private cryptoService: CryptoService,
    private progressionService: ProgressionService,
  ) {
  }

  ngOnInit(): void {
    this.fileTypes = this.fileService.getUploadFileTypes();

    if (this.attachmentsService.open) {
      this.attachmentsService.open = false;
      this.attachmentsService.activeAction = null;
    }

    const currentUser = this.authService.user;
    this.externalId = currentUser ? currentUser.hsdpId : null;
    this.organization = currentUser ? currentUser.organization : null;
    this.userId = currentUser ? (currentUser.info ? currentUser.info.id : null) : null;

    if (currentUser && currentUser.xsrf) {
      this.xsrf = currentUser.xsrf;
    }

    if (this.getParameterByName('u-code')) {
      this.xsrf = this.getParameterByName('u-code');
    }

    this.$subscription.add(
      this.diagnosticOrderActionsService.getTriggerLetterGenerate().subscribe((data: IDocument[]) => {
        this.files = data;
        this.lists = this.escapeHtml([...this.files]);
        this.postReportToEmrOnGenerateLetterEnabled = this.testService?.testConfiguration?.taskDispatchTemplates?.filter(
          (e: any) => e
            && e.type
            && e.type === 'postReportToEMR'
            && e.reference
            && e.settings
            && e.settings.enableOnGenerateLetter
        ).length > 0;

        if (this.postReportToEmrOnGenerateLetterEnabled) {
          this.attachmentsService.postToEmr(this.workflowId, this.currentRevisionData.id, data[0])
            .subscribe(
              () => {
                this.notificationMessageService.success(data[0].friendlyName + ' was successfully scheduled for posting to EMR');
              },
              () => {
                this.notificationMessageService.error('Failed posting ' + data[0].friendlyName + ' to EMR');
              }
            );
        }
      })
    );

    this.$subscription.add(
      this.diagnosticOrderActionsService.getTriggerUpdateAttachments().subscribe((data: IDocument[]) => {
        this.files = data;
        this.lists = this.escapeHtml([...this.files]);
      })
    );

    if (!this.attachmentsService.maxUploadedFile) {
      this.attachmentsService.getMaxUploadedFilesAllowedPerTest()
        .subscribe(
          (res: ExtendedHttpResponse<any>) => {
            this.maxUploadedFile = +res.body.maxUploadedFiles;
            this.attachmentsService.maxUploadedFile = +res.body.maxUploadedFiles;
          },
          () => {
            this.maxUploadedFile = 10;
            this.attachmentsService.maxUploadedFile = 10;
          }
        );
    } else {
      this.maxUploadedFile = this.attachmentsService.maxUploadedFile;
    }

    this.diagnosticOrderActionsService.getState()
      .pipe(
        take(1),
        map(state => state.order),
        map(currentTest => {
          this.ordererReference = currentTest.ordererReference;
          this.testNumber = currentTest.identifiers[0].value;
          this.init();

          /* Check if attached report SHALL be posted to EMR */
          this.postReportToEmrEnabled = this.testService?.testConfiguration?.taskDispatchTemplates?.filter(
            (e: any) => e
              && e.type
              && e.type === 'postReportToEMR'
              && e.reference
              && e.settings
              && e.settings.enabled
          ).length > 0;

          if (this.postReportToEmrEnabled) {
            let postReportToEmrEnabled = false;
            let numberOfCurrentRevision = 1;
            const countRevisions = this.testService.patientTests.current.workflowRequest.revisions.length;

            this.testService.patientTests.current.workflowRequest.revisions.forEach((revision: any, idx: number) => {
              if (revision.id === this.currentRevisionData.id) {
                numberOfCurrentRevision = idx + 1;
              }
            });

            if (this.testService.patientTests.current.workflowRequest.activeRevision === this.currentRevisionData.id) {
              postReportToEmrEnabled = true;
            }

            // when test has status -test completed in the workflowRequest.activeRevision has None!
            // but the active revision is always the latest revision
            if (this.testService.patientTests.current.workflowRequest.activeRevision.toLowerCase() === 'none' && countRevisions === numberOfCurrentRevision) {
              postReportToEmrEnabled = true;
            }

            if (postReportToEmrEnabled === false) {
              this.postReportToEmrEnabled = false;
            }
          }

          if (currentTest.testConfigurations) {
            const org = currentTest.testConfigurations && currentTest.testConfigurations
              .find(elem => elem.organization && elem.organization !== this.authService.user.organization);

            if (org) {
              this.shareWithOrganizationId = org.organization;
            }
          }

          return this.shareWithOrganizationId;
        }),
        filter(x => x !== undefined),
        map((shareWithOrganizationId) => {
          if (shareWithOrganizationId) {
            this.userActionCreatorService.getOrganizationConfiguration(shareWithOrganizationId).take(1)
              .subscribe(organization => {
                let item: any;

                for (const key in this.actionsLabelObject) {
                  item = this.actionsLabelObject[key];

                  if (item.shouldUpdate) {
                    item.text = this.buildLabelText(item.text, item.delimiterWord, organization.name);
                    item.shouldUpdate = false;
                  }
                }
              });
          }
        }),
      )
      .subscribe(() => {
      });

    this.files && this.files
      .filter((file: IDocument) => file.metaData.generateDownloadUrl === 'true' && !file.metaData.externalUrl)
      .forEach((file: IDocument) => {
        this.attachmentsService.generateDownloadURL(file.id)
          .subscribe((fileUrl: string) => {
            file.metaData.externalUrl = fileUrl;
          });
      });

    this.initDzOptions();
    this.initActions();
  }

  private initDzOptions() {
    if (!this.attachmentsService.maxFilesize) {
      this.attachmentsService.getMaxFileSizeAllowedForUpload()
        .subscribe(
          (res: ExtendedHttpResponse<any>) => {
            this.attachmentsService.maxFilesize = +res.body.maxFileSize;
          },
          () => {
            this.attachmentsService.maxFilesize = 10;
          }
        );
    }
  }

  private initActions() {
    this.actions = [
      {
        label: 'download',
        class: 'download Auto_Download',
        icon: 'icon__download',
        visibleCondition: () => true,
        onClick: (file: IDocument) => {
          this.coreActionCreator.setLoader(true);

          if (file.metaData.generateDownloadUrl === 'true') {
            const externalUrlObs$ = file.metaData.externalUrl ? of(file.metaData.externalUrl) : this.attachmentsService.generateDownloadURL(file.id);
            externalUrlObs$.pipe(
              catchError(
                () => {
                  this.coreActionCreator.setLoader(false);
                  return EMPTY;
                })
            ).subscribe((fileUrl: string) => {
              file.metaData.externalUrl = fileUrl;
              this.download(file);
              this.coreActionCreator.setLoader(false);
            })
          } else {
            this.download(file);
            this.coreActionCreator.setLoader(false);
          }

          this.attachmentsService.activeAction = null;
        }
      },
      {
        label: 'copyUrl',
        class: 'copyUrl Auto_Copy_Url',
        icon: 'icon__Link',
        visibleCondition: (file: IDocument) => file.metaData.generateDownloadUrl === 'true',
        onClick: (file: IDocument) => {
          const externalUrlObs$ = file.metaData.externalUrl
            ? of(file.metaData.externalUrl)
            : this.attachmentsService.generateDownloadURL(file.id);

          externalUrlObs$.pipe(
            catchError(
              () => {
                this.notificationMessageService.error('Failed to copy link');
                return EMPTY;
              }
            )
          ).subscribe((fileUrl: string) => {
            file.metaData.externalUrl = fileUrl;
            let copyUrl: any;
            copyUrl = document.querySelector('#attachments-copy-url');
            copyUrl.value = fileUrl;
            copyUrl.select();
            document.execCommand('copy')
              ? this.notificationMessageService.success('The link has been copied')
              : this.notificationMessageService.error('Failed to copy link');
          })

          this.attachmentsService.activeAction = null;
        }
      },
      {
        label: 'share',
        class: 'gain-access Auto_Share',
        icon: 'fa fa-unlock',
        visibleCondition: (file: IDocument) => this.shareAvailable() && !(file.metaData?.shared === 'true') && this.isOwnerOrganization(file),
        onClick: (file: IDocument) => {
          if (!(file.id && this.shareWithOrganizationId && this.ordererReference.revisionId)) {
            return;
          }

          this.attachmentsService.addOrganizationToFileContext(file.id, this.shareWithOrganizationId, this.ordererReference.revisionId)
            .subscribe(
              () => {
                this.attachmentsService.activeAction = null;
                file.metaData.shared = 'true';
                this.notificationMessageService.success('File shared successfully');
              },
              () => {
                this.notificationMessageService.error('File share failed');
              }
            );
        }
      },
      {
        label: 'restrict',
        class: 'restrict Auto_Restrict',
        icon: 'fa fa-lock',
        visibleCondition: (file: IDocument) => this.shareAvailable() && (file.metaData.shared && file.metaData.shared === 'true') && this.isOwnerOrganization(file),
        onClick: (file: IDocument) => {
          if (!(file.id && this.shareWithOrganizationId && this.ordererReference.revisionId)) {
            return;
          }

          this.attachmentsService.removeOrganizationToFileContext(file.id, this.shareWithOrganizationId, this.ordererReference.revisionId)
            .subscribe(
              () => {
                file.metaData.shared = 'false';
                this.attachmentsService.activeAction = null;
                this.notificationMessageService.success('File share stopped');
              },
              () => {
                this.notificationMessageService.error('Stop sharing failed');
              }
            );
        }
      },
      {
        label: 'postToEmr',
        class: 'download Auto_PostToEmr',
        icon: 'icon__postToEmr',
        visibleCondition: (file: IDocument) => !!this.postReportToEmrEnabled && file.metaData && file.metaData?.generatedBy?.includes('released'),
        onClick: (file: IDocument) => {
          this.attachmentsService.postToEmr(this.workflowId, this.currentRevisionData.id, file)
            .subscribe(
              () => {
                this.notificationMessageService.success(file.friendlyName + ' was successfully scheduled for posting to EMR');
              },
              () => {
                this.notificationMessageService.error('Failed posting ' + file.friendlyName + ' to EMR');
              }
            );
          this.attachmentsService.activeAction = null;
        }
      },
      {
        label: 'delete',
        class: 'delete Auto_Delete',
        icon: 'icon__DeleteTrash',
        visibleCondition: (file: IDocument) => file.metaData && ['converted', 'uploaded'].indexOf(file.metaData.generatedBy) > -1 && file.context.find(c => c.reference === this.authService.user.id),
        onClick: (file: IDocument) => {
          if (!file.metaData || ['converted', 'uploaded'].indexOf(file.metaData.generatedBy) === -1 || !(file.context.find(c => c.reference === this.authService.user.id))) {
            return;
          }

          const text = `Are you sure you want to remove the current ${file.friendlyName} from Reports & Documents`;
          this.modalLoader.popupModal(ConfirmModalComponent,
            {
              title: 'Remove the document from Reports & Documents list',
              content: text,
              approveBtn: 'Yes',
              declineBtn: 'No'
            }, ModalTypes.NONE)
            .subscribe((response) => {
              if (response && response.payload === 1) {
                this.coreActionCreator.setLoader(true);
                this.attachmentsService.deleteUploadedFile(file.id)
                  .subscribe(
                    () => {
                      this.attachmentsService.activeAction = null;
                      this.loadDocuments('remove');
                    },
                    () => {
                      this.coreActionCreator.setLoader(false);
                      this.notificationMessageService.error('Document didn\'t remove from your Reports & Documents list successfully!');
                    }
                  );
              }
            });
        }
      },
    ];
  }

  private shareAvailable(): any {
    return this.shareWithOrganizationId &&
      this.ordererReference.revisionId &&
      this.testService.testConfiguration?.fileSharing?.manualAllowed;
  }

  private isOwnerOrganization(file: IDocument): boolean {
    return !file.ownerOrganization || file.ownerOrganization === this.organization;
  }

  private download(file: IDocument) {
    const id = file.id, friendlyName = file.friendlyName, fileSize = file.size, maxFileSize = 50000000
    if(fileSize>maxFileSize) {
      this.coreActionCreator.setLoader(false);
      const appName = sessionStorage.getItem('appName');
      if(appName=='Genomics Workspace')
        this.notificationMessageService.warning('The file exceeds the maximum size for download 50MB. Please use the URL within "Reports & Documents" to access it.');
      else
        this.notificationMessageService.warning('The file exceeds the maximum size for download 50MB.');
    } else {
      this.workflowService.getReportData(id)
        .subscribe(
          (file) => {
            this.pdfService.downloadPDF(file, friendlyName, id);
            this.coreActionCreator.setLoader(false);
          },
          () => {
            this.coreActionCreator.setLoader(false);
            this.notificationMessageService.error('unable to download file');
          }
        );
    }
  }

  private getParameterByName(name: string): any {
    const match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);

    return match && decodeURIComponent(decodeURIComponent(match[1].replace(/\+/g, ' ')));
  }

  private init() {
    this.headers['Cache-Control'] = 'no-cache';

    if (this.organization) {
      this.headers.external_id = this.externalId;
      this.headers.organization = this.organization;
      this.headers.context = this.currentRevisionData.id;
      this.headers.xsrf = this.xsrf;
      this.headers.user_id = this.userId;
      this.headers.test_number = this.testNumber;
      this.headers.workflow_Id = this.workflowId;
    }

    this.attachmentsService.open = false;
    this.loadDocuments();
  }

  private escapeHtml(file: IDocument[]): IDocument[] {
    file.forEach(fl => {
      if (fl['metaData'] && fl['metaData'].changeFileName) {
        fl['metaData'].changeFileName = StringUtils.escapeHtml(fl['metaData'].changeFileName);
      }

      if (fl['friendlyName']) {
        fl['friendlyName'] = StringUtils.escapeHtml(fl['friendlyName']);
      }
    });

    return file;
  }

  private loadDocuments(type?: string) {
    const app = JSON.parse(sessionStorage.getItem('currentApplication'));

    this.documentsLoaded.emit(false);
 // since its per app this can be cached
    const forkObj = [
      (app && app.id) ? this.attachmentsService.getDocuments('[' + app.id + ',commonDocument]',true) : of([]),
      this.attachmentsService.getDocuments(this.currentRevisionData.id, false, true),
      this.currentRevisionData.status === 'Navigation Complete'
        ? this.progressionService.search('PATHWAY_TEST').pipe(take(1))
        : of(false)
    ];

    this.lists.length = 0;

    forkJoin(forkObj)
      .pipe(
        take(1)
      )
      .subscribe(
        async ([docsCommon, docsRegular, progressions]) => {
          const files = docsCommon && docsCommon['body'] ? docsCommon['body'] : [];
          let commonDocs = [];

          if (files && files.length > 0) {
            if (progressions) {
              // status is "Navigation Complete"
              const prog = progressions as Progression[];
              let ids: string[] = [];

              if (prog.length > 0 && prog[0].data.commonDocuments) {
                ids = [...prog[0].data.commonDocuments];
              }

              commonDocs = files.filter((fl: any) => ids.includes(fl.id));
            } else {
              commonDocs = files.filter((fl: any) => fl.active);
              this.progressionService.setCommonDocuments(commonDocs.map(doc => doc.id));
            }

            this.lists = this.escapeHtml([...commonDocs]);
          }

          this.files = docsRegular && docsRegular['body'] ?
            (docsRegular['body'].data ? docsRegular['body'].data : docsRegular['body']) : [];

          if (this.files && this.files.length > 0) {
            this.files = this.files.filter(fl => {
              return !fl.context.find(ctx => ctx.resourceType === 'commonDocument');
            })

            if (!this.lists) {
              this.lists = [];
            }

            this.lists = [...this.lists, ...this.escapeHtml(this.files)];
          }

          this.documentsLoaded.emit(true);

          if (type === 'remove') {
            this.coreActionCreator.setLoader(false);
            this.notificationMessageService.success('Document removed from your Reports & Documents list successfully!');
          } else if (type === 'upload') {
            clearInterval(this.interval);
            this.value = null;
            this.changeDetect.detectChanges();
          }
        },
        (error) => {
          this.documentsLoaded.emit(true);
          console.log(error);
        }
      );
  }

  private buildLabelText(prefix: string, delimiter: string, organization: string): any {
    return prefix + delimiter + organization;
  }

  public findIcon(fileType: string): string {
    if (this.fileTypes['pdf'] && this.fileTypes['pdf'].indexOf(fileType) !== -1) {
      return 'icon__pdf';
    } else if (this.fileTypes['text'] && this.fileTypes['text'].indexOf(fileType) !== -1) {
      return 'icon__word';
    } else if (this.fileTypes['image'] && this.fileTypes['image'].indexOf(fileType) !== -1) {
      return 'icon__image';
    } else {
      return 'icon__attachment';
    }
  }

  public viewPdf(id: string, friendlyName: string) {
    this.coreActionCreator.setLoader(true);
    this.workflowService.getReportData(id)
      .subscribe(
        (file) => {
          this.pdfService.openPDF(file, friendlyName, id);
        },
        () => {
          this.notificationMessageService.error('Unable to retrieve the pdf');
        },
        () => {
          this.coreActionCreator.setLoader(false);
        }
      )
  }

  public getUploadedByText(file: IDocument): string {
    const key = 'attachment.' + file.metaData.generatedBy;
    const translation = this.translateService.instant(key);

    return translation === key ? this.getUploadedByTextEng(file) : translation + ' ' + file.actorName;
  }

  private getUploadedByTextEng(file: IDocument): string {
    // TODO: remove this function when translation will be added to dictionaries
    let outputText: string = 'Uploaded by Admin';

    switch (file.metaData.generatedBy) {
      case 'uploadedBySysAdmin':
      case 'GenomicsAdminGroup':
        outputText = 'Uploaded by System Admin';
        break;
      case 'uploadedByOrgAdmin':
        outputText = 'Uploaded by Organization Admin';
        break;
    }

    return outputText;
  }

  public isCommonDoc(file: IDocument): boolean {
    return !!file.context.find(cnt => cnt.reference === 'commonDocument');
  }

  public getActions(file: IDocument) {
    return this.actions.filter(item => item.visibleCondition(file));
  }

  public onUpload(event) {
    this.file = event.addedFiles[0];

    if (this.fileService.isForbiddenFileTypes(this.file.name, this.fileTypes)) {
      return;
    }

    if (StringUtils.isInvalidCharsInFileName(this.file.name)) {
      this.notificationMessageService.error(`${this.notificationMessage.FILENAME_CONTAINS_HTML}`);
      return;
    }

    if (this.value) {
      this.notificationMessageService.error('You can\'t upload multiple files');
      return;
    }

    const fileSize = (Math.round(this.file.size / 1024 / 10.24)) / 100;
    const filesizeBase = 1000;
    let _i: any;
    let selectedSize = 0;
    let selectedUnit = 'b';

    if (this.file.size > 0) {
      const units = ['TB', 'GB', 'MB', 'KB', 'b'];
      for (let i = _i = 0, _len = units.length; _i < _len; i = ++_i) {
        const unit = units[i];
        const cutoff = Math.pow(filesizeBase, 4 - i) / 10;
        if (this.file.size >= cutoff) {
          selectedSize = this.file.size / Math.pow(filesizeBase, 4 - i);
          selectedUnit = unit;
          break;
        }
      }
      selectedSize = Math.round(10 * selectedSize) / 10;
    }

    this.apxFileSz = selectedSize + ' ' + selectedUnit;

    if (this.file.size === 0) {
      this.fileSizeErrorMessage = 'Empty files will not be uploaded.';
      setTimeout(() => {
        this.fileSizeErrorMessage = null;
        this.changeDetect.markForCheck();
      }, 2000);
    } else if (this.file.size > this.attachmentsService.maxFilesize * 1024 * 1024) {
      this.fileSizeErrorMessage = `File is too big (\ ${fileSize}\MiB). Max filesize: \ ${this.attachmentsService.maxFilesize}\MiB.`;
      setTimeout(() => {
        this.fileSizeErrorMessage = null;
        this.changeDetect.markForCheck();
      }, 2000);
    } else {
      const xhr = new XMLHttpRequest();
      this.file.xhr = xhr;
      xhr.open('post', this.attachmentsService.baseUrl, true);

      const user = this.authService.user;
      const hash = (user && user.permissions)
        ? this.cryptoService.hashSha256(JSON.stringify(user.permissions.sort()) + '_' + (user.roleId || ''))
        : undefined;
      const headers = {
        ...{
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'X-Requested-With': 'XMLHttpRequest',
          'aux_token': hash
        },
        ...this.headers
      };

      for (const headerName in headers) {
        const headerValue = headers[headerName];

        if (headerValue) {
          xhr.setRequestHeader(headerName, headerValue);
        }
      }

      const formData = new FormData();
      formData.append('file', this.file, this.file.name);

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          switch (xhr.status) {
            case 200:
              this.loadDocuments('upload');
              break;

            case 401:
              this.handleError('Unauthorized!');
              break;

            default:
              this.handleError('Failed to upload file.');
          }
        }
      };
      xhr.send(formData);

      this.value = 10;
      this.updateProgress();
      this.interval = setInterval(() => {
        if (this.value < 80) {
          this.value = this.value + 20;
          this.updateProgress();
        } else {
          clearInterval(this.interval);
        }
      }, 2000);
    }

  }

  private updateProgress() {
    this.changeDetect.detectChanges();
    const el = document.getElementById('progress');

    if (el) {
      el.style.width = this.value.toString() + '%';
    }
  }

  private handleError(message: string) {
    this.value = null;
    clearInterval(this.interval);
    this.notificationMessageService.error(message);
    this.changeDetect.detectChanges();
  }

  public fileId(index: number, file: IDocument): any {
    return file.fileType;
  }

  public ngOnDestroy() {
    this.progressionService.setCommonDocuments([]);
    this.$subscription.unsubscribe();
  }
}
